<template>
  <div
    class="relative mx-auto space-y-5 rounded-t-md rounded-b-md bg-white p-5 shadow-md"
    :class="{ 'border-2 border-red-600': attribute.errors?.length }"
    data-cy="attribute-card"
  >
    <div class="flex items-center justify-between">
      <div class="flex max-w-145 grow items-center gap-3">
        <lf-heading3 v-tooltip="attribute.name" class="break-words">
          {{ attribute.name }}
        </lf-heading3>
        <attribute-base-modal
          :attribute="attribute"
          force-new
          @attribute:saved="emit('attribute:edit', $event)"
        >
          <template
            v-if="!disabled && !attribute.is_readonly"
            #default="{ toggleModal }"
          >
            <button @click="toggleModal">
              <icon-base
                :icon="IconEdit"
                class="cursor-pointer text-disabled-medium"
              />
            </button>
          </template>
        </attribute-base-modal>
        <lf-tag
          :class="{
            'bg-green-100 text-green-600':
              attribute.status === CustomAttributeStatusOption.published,
          }"
        >
          {{ attributeStatusOptions[attribute.status] }}
        </lf-tag>
        <icon-base
          v-if="attribute.description"
          v-tooltip="attribute.description"
          :icon="IconInfo"
          class="text-disabled-medium"
        />
      </div>
      <div class="flex items-center space-x-2">
        <button
          v-tooltip="
            isPrettyMode
              ? t('ATTRIBUTE_BUILDER.COMPACT_FORMULA')
              : t('ATTRIBUTE_BUILDER.FORMAT_FORMULA')
          "
          class="rounded-sm p-2 pb-1"
          data-cy="toggle-nested-view"
          :class="
            isPrettyMode ? 'bg-primary text-white' : 'bg-blue-50 text-primary'
          "
          @click="isPrettyMode = !isPrettyMode"
        >
          <icon-base :icon="IconCode" class="h-5 w-5" />
        </button>
        <attributes-action-menu
          v-if="!disabled"
          :actions="{
            publish: true,
            draft: attribute.status === CustomAttributeStatusOption.draft,
            delete: canDelete,
          }"
          :model="attribute"
          @attribute:delete="emit('attribute:delete', $event)"
          @attribute:save="emit('attribute:save', $event)"
        />
      </div>
    </div>
    <div class="flex items-center justify-between gap-3">
      <formula-editor
        ref="formulaEditor"
        v-model="contentProxy"
        v-model:is-pretty-mode="isPrettyMode"
        :placeholder="t('ATTRIBUTE_BUILDER.PLACEHOLDER')"
        :disabled="disabled || attribute.is_readonly"
        class="w-full"
        @focus="emit('editor:focus')"
      />
      <div
        v-if="isEvaluationMode"
        class="flex items-center justify-between gap-3"
      >
        <span class="text-gray-700">=</span>
        <lf-input
          :placeholder="$t('COMMON.RESULT')"
          :value="formattedResult"
          name="attribute_result"
          data-cy="attribute-result"
          no-margin
          :has-padding="false"
          readonly
        >
          <template #suffix>
            <icon-base
              v-if="isEvaluating"
              :icon="IconLoadingCircle"
              class="h-4 w-4 animate-spin rounded-full text-primary"
              view-box="0 0 20 20"
            />
            <icon-base
              v-else-if="evaluated"
              :icon="IconCheck"
              class="h-4 w-4 text-success-button-active"
            />
          </template>
        </lf-input>
      </div>
    </div>
    <div
      v-if="attribute.errors?.length"
      class="text-xxs text-red-700 sm:text-xs"
    >
      {{ attribute.errors.join(", ") }}
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";

import { CustomAttributeStatusOption } from "@/enums/customAttributes";
import { isBaselayerBusinessRegistrationsAttribute } from "@/helpers/baselayer";
import {
  formatNumberWithCommas,
  truncateWithEllipsis,
} from "@/helpers/formatting";
import { isOcrolusFullResponseAttribute } from "@/helpers/ocrolus";
import { getFullResponseDebtSchedulePlaceholder } from "@/helpers/services";
import { useAttributeBuilderStore } from "@/stores/customAttributeBuilder";

import AttributeBaseModal from "@/components/customAttributes/AttributeBaseModal.vue";
import AttributesActionMenu from "@/components/customAttributes/AttributesActionMenu.vue";
import FormulaEditor from "@/components/customAttributesV2/FormulaEditor.vue";
import IconCheck from "@/components/icons/IconCheck.vue";
import IconCode from "@/components/icons/IconCode.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import IconInfo from "@/components/icons/IconInfo.vue";
import IconLoadingCircle from "@/components/icons/IconLoadingCircle.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import LfHeading3 from "@/components/ui/text/LfHeading3.vue";
import LfTag from "@/components/ui/text/LfTag.vue";

import type { CustomAttribute } from "@/models/customAttributes/customAttribute";
import type { IAttribute } from "@/models/orchestration";

const emit = defineEmits<{
  "attribute:edit": [CustomAttribute];
  "attribute:update": [CustomAttribute];
  "attribute:delete": [CustomAttribute["id"]];
  "attribute:save": [CustomAttributeStatusOption];
  "editor:focus": [];
}>();

const {
  attribute,
  canDelete,
  isEvaluationMode,
  evaluated,
  isEvaluating,
  disabled,
} = defineProps<{
  attribute: CustomAttribute;
  canDelete?: boolean;
  isEvaluationMode?: boolean;
  evaluated: boolean;
  isEvaluating: boolean;
  disabled: boolean;
}>();
const { t } = useI18n();
const attributesStore = useAttributeBuilderStore();

const { attributeStatusOptions } = storeToRefs(attributesStore);

const isPrettyMode = ref(false);
const formulaEditorRef = useTemplateRef("formulaEditor");

const contentProxy = computed({
  get: () => attribute.tokens || [],
  set: (value) => {
    const updatedAttribute: CustomAttribute = {
      ...attribute,
      tokens: value,
    };
    emit("attribute:update", updatedAttribute);
  },
});

const formattedResult = computed(() => {
  if (isOcrolusFullResponseAttribute(attribute.result)) {
    return t(
      "DEALS.DEAL_PROGRESS.UNDERWRITING.OCROLUS.SERVICE_RUN_ATTRIBUTE_PLACEHOLDER"
    );
  }

  if (isBaselayerBusinessRegistrationsAttribute(attribute.result)) {
    return t("DEALS.BASELAYER.BUSINESS_REGISTRATIONS_JSON");
  }

  const fullResponseDebtSchedulePlaceholder =
    getFullResponseDebtSchedulePlaceholder(attribute.result);

  if (fullResponseDebtSchedulePlaceholder) {
    return fullResponseDebtSchedulePlaceholder;
  }

  if (typeof attribute.result === "number") {
    return formatNumberWithCommas(attribute.result, {
      style: "decimal",
      maximumFractionDigits: 17,
    });
  }
  return truncateWithEllipsis(String(attribute.result), 20);
});

defineExpose({
  insertAttribute: (attr: IAttribute) =>
    formulaEditorRef.value?.insertAttribute(attr),
});
</script>
